import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Check,
  FileText,
  Info,
  Plus,
  Printer,
  RotateCcw,
  Trash2,
  Wallet,
  XCircle,
} from 'lucide-react'
import { getAccounts, getControlAccounts, getCustomers, getProducts } from '../../api/master'
import DefaultAccountSelect, { useDefaultChoices } from '../../components/DefaultAccountSelect'
import {
  createCustomerCreditMemo,
  getCustomerInvoice,
  postCustomerInvoice,
  saveCustomerInvoice,
  voidCustomerInvoice,
} from '../../api/sales'
import { getCompany } from '../../api/settings'
import ErrorBanner from '../../components/ErrorBanner'
import { HelpTip } from '../../components/HelpTip'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import Spinner from '../../components/Spinner'
import { useToast } from '../../components/Toast.jsx'
import { fmtDate, fmtDay, localToday, parseWallClock, startOfToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import DocumentPrint from '../../components/DocumentPrint'
import { money } from './salesShared'
import { jobsOnly, useSession } from '../../session'
import JobSelect from '../../components/JobSelect'
import { jobDeposits } from '../../api/jobs'
import { useStandardDefaults } from '../accounting/accountingShared'
import { CreateOption } from '../../components/CreateOption'
import { printPage, useAutoPrint } from '../../utils/print'

const todayIso = localToday
const moneyInput = (value) => Number(value || 0).toFixed(2)
const num = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}
const round2 = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100

const addDays = (date, days) => {
  const [y, m, d] = String(date || '').slice(0, 10).split('-').map((part) => parseInt(part, 10))
  if (!y || !m || !d) return ''
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + Number(days || 0))
  return dt.toISOString().slice(0, 10)
}

const dueDateForTerms = (invoiceDate, terms) => {
  const t = String(terms || '').toLowerCase()
  const match = /net\s*(\d+)/.exec(t)
  if (match) return addDays(invoiceDate, parseInt(match[1], 10))
  if (t.includes('due on receipt')) return invoiceDate
  return ''
}

const customerLabel = (customer) => {
  const name = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim()
  return name || customer?.email || customer?.phone || `Customer #${customer?.customer_id}`
}

const uploadedAssetSrc = (path) => {
  const p = String(path || '').trim()
  if (!p) return ''
  if (/^(https?:)?\/\//i.test(p) || p.startsWith('data:')) return p
  return `${import.meta.env.VITE_API_URL || ''}${p}`
}

const preloadImage = (src) => new Promise((resolve) => {
  if (!src || typeof Image === 'undefined') {
    resolve()
    return
  }
  const img = new Image()
  let settled = false
  const done = () => {
    if (settled) return
    settled = true
    resolve()
  }
  img.onload = done
  img.onerror = done
  img.src = src
  setTimeout(done, 1200)
})

const isServiceItem = (product) => Number(product?.is_service || 0) === 1

// '' revenue account = the default for the line's type (products to Sales,
// services to Service Revenue).
const emptyLine = (lineType = 'Service') => ({
  product_id: '',
  item_name: '',
  description: '',
  quantity: '1',
  unit_price: '0.00',
  discount_amount: '0.00',
  tax_rate: '0',
  revenue_account_code: '',
  line_type: lineType,
})

const normalizeLine = (line) => ({
  product_id: line.product_id ? String(line.product_id) : '',
  item_name: line.item_name || line.product_name || '',
  description: line.description || '',
  quantity: String(line.quantity ?? line.qty ?? '1'),
  unit_price: String(line.unit_price ?? '0.00'),
  discount_amount: String(line.discount_amount ?? '0.00'),
  tax_rate: String(line.tax_rate ?? '0'),
  revenue_account_code: line.revenue_account_code || '',
  line_type: line.line_type === 'Product' ? 'Product' : 'Service',
})

export default function CustomerInvoiceForm() {
  // A new invoice from a job's page arrives with ?job=&customer=.
  const [linkParams] = useSearchParams()
  const { has, modules } = useSession()
  const jobOnly = jobsOnly(modules)
  const books = has('accounting') // the plan has Accounting: show accounts and journal entries
  const nav = useNavigate()
  const { invoiceId } = useParams()
  const { notify } = useToast()
  const isNew = !invoiceId

  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [accounts, setAccounts] = useState([])
  const [controls, setControls] = useState({})
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [posting, setPosting] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const [crediting, setCrediting] = useState(false)
  const [formError, setFormError] = useState('')
  const [voidOpen, setVoidOpen] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [creditOpen, setCreditOpen] = useState(false)
  const [creditForm, setCreditForm] = useState({
    credit_date: todayIso(),
    reason: '',
    memo: '',
    amount: '',
    tax_rate: '0',
    revenue_account_code: '',
  })
  const [invoice, setInvoice] = useState({
    invoice_id: null,
    invoice_number: '',
    customer_id: linkParams.get('customer') || '',
    job_id: linkParams.get('job') || '',
    invoice_kind: 'Standard',
    deposit_applied: '',
    retainage_amount: '',
    invoice_date: todayIso(),
    due_date: '',
    terms: 'Net 30',
    memo: '',
    status: 'Draft',
    je_id: null,
    source_quote_id: null,
    ar_account_code: '',
  })
  const [lines, setLines] = useState([emptyLine()])
  const [showAccounts, setShowAccounts] = useState(false)
  const [settlements, setSettlements] = useState({ payments: [], credits: [] })
  const std = useStandardDefaults()

  const revenueAccounts = useMemo(() => {
    const rows = accounts.filter((acc) => String(acc.type || '').toLowerCase() === 'revenue')
    return rows.length ? rows : accounts
  }, [accounts])

  const defaultRevenue = useMemo(() => {
    const has = (code) => code && revenueAccounts.some((acc) => String(acc.account_code) === String(code))
    if (has(controls.Sales)) return controls.Sales
    // Not set: the standard Sales account, as the server does.
    if (has(std.Sales)) return std.Sales
    return revenueAccounts[0]?.account_code || std.Sales || ''
  }, [revenueAccounts, controls, std])
  const serviceRevenue = useMemo(() => (
    controls.ServiceRevenue && revenueAccounts.some((acc) => String(acc.account_code) === String(controls.ServiceRevenue))
      ? controls.ServiceRevenue : defaultRevenue
  ), [revenueAccounts, controls, defaultRevenue])
  // A line's revenue account unless one is chosen: the item's own income
  // account, else the default for products or services.
  const productById = useMemo(() => Object.fromEntries(products.map((p) => [String(p.product_id), p])), [products])
  const revenueFor = (line) => {
    const own = line.product_id && productById[String(line.product_id)]?.income_account_code
    if (own && revenueAccounts.some((acc) => String(acc.account_code) === String(own))) return own
    return line.line_type === 'Service' ? serviceRevenue : defaultRevenue
  }
  // Which receivables account the invoice is on, when there's more than one.
  const receivables = useDefaultChoices('AR')
  const stockItems = useMemo(() => products.filter((p) => !isServiceItem(p)), [products])
  const serviceItems = useMemo(() => products.filter(isServiceItem), [products])
  const newLineType = stockItems.length ? 'Product' : 'Service'

  const selectedCustomer = useMemo(
    () => customers.find((customer) => String(customer.customer_id) === String(invoice.customer_id)),
    [customers, invoice.customer_id]
  )
  const customerName = selectedCustomer ? customerLabel(selectedCustomer) : (invoice.customer_id ? `Customer #${invoice.customer_id}` : 'No customer selected')
  const statusLabel = invoice.status || 'Draft'
  const statusLower = String(statusLabel || '').toLowerCase()
  const invoiceOpenBalance = num(invoice.balance_amount ?? invoice.total_amount)
  const paidToDate = num(invoice.paid_to_date)
  const creditedToDate = num(invoice.credited_to_date)
  const canRecordPayment = ['posted', 'partially paid'].includes(statusLower) && invoiceOpenBalance > 0.004
  const canCreateCreditMemo = ['posted', 'partially paid'].includes(statusLower) && invoiceOpenBalance > 0.004
  const canVoidInvoice = statusLower === 'posted' && paidToDate <= 0.004 && creditedToDate <= 0.004
  const readOnly = statusLower !== 'draft'
  // Deposit and stage invoices keep the lines they were made with.
  const kind = invoice.invoice_kind || 'Standard'
  const linesLocked = readOnly || kind === 'Deposit' || kind === 'Progress' || kind === 'Retainage'
  const kindLabel = kind === 'Deposit' ? 'Deposit' : kind === 'Retainage' ? 'Retainage release'
    : kind === 'Progress' ? `Stage: ${Number(invoice.progress_percent || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}% of ${invoice.source_quote_number || 'the quote'}` : ''
  const invoiceNumber = invoice.invoice_number || (isNew ? 'New invoice' : 'Draft invoice')
  const dueDay = parseWallClock(invoice.due_date)
  const badge = (() => {
    if (statusLower === 'void' || statusLower === 'voided') return { label: 'Void', tone: 'bad' }
    if (statusLower === 'draft' || !statusLower) return { label: 'Draft', tone: 'muted' }
    if (statusLower === 'paid' || invoiceOpenBalance <= 0.004) return { label: 'Paid', tone: 'good' }
    const partPaid = statusLower === 'partially paid' || paidToDate + creditedToDate > 0.004
    if (dueDay && dueDay < startOfToday()) return { label: partPaid ? 'Overdue, part paid' : 'Overdue', tone: 'warn' }
    return { label: partPaid ? 'Part paid' : 'Unpaid', tone: 'info' }
  })()
  const isDueOnReceipt = String(invoice.terms || '').toLowerCase().includes('due on receipt')
  const isSettled = readOnly && invoiceOpenBalance <= 0.004 && ['paid', 'void'].includes(statusLower)
  const paymentInstruction = isSettled
    ? 'This invoice is fully settled. No payment is due.'
    : isDueOnReceipt
    ? 'Please remit payment upon receipt of this invoice.'
    : 'Please remit payment by the due date listed above.'
  const companyName = company?.company_name || 'ASYS Retail'
  const companyLogoSrc = uploadedAssetSrc(company?.logo_path)
  const companyAddress = [company?.address].filter(Boolean)
  const companyContact = [company?.phone, company?.email, company?.website].filter(Boolean)
  const customerAddress = [
    selectedCustomer?.address,
    [selectedCustomer?.city, selectedCustomer?.state, selectedCustomer?.postal_code].filter(Boolean).join(', '),
    selectedCustomer?.country,
  ].filter(Boolean)
  const customerContact = [selectedCustomer?.phone, selectedCustomer?.email].filter(Boolean)

  const applyServerInvoice = (data) => {
    const header = data?.header || {}
    setInvoice({
      invoice_id: header.invoice_id || data?.invoice_id || null,
      invoice_number: header.invoice_number || '',
      customer_id: header.customer_id ? String(header.customer_id) : '',
      job_id: header.job_id ? String(header.job_id) : '',
      invoice_kind: header.invoice_kind || 'Standard',
      deposit_applied: num(header.deposit_applied) > 0 ? String(num(header.deposit_applied)) : '',
      retainage_amount: num(header.retainage_amount) > 0 ? String(num(header.retainage_amount)) : '',
      progress_percent: header.progress_percent,
      source_quote_number: header.source_quote_number,
      invoice_date: fmtDate(header.invoice_date) || todayIso(),
      due_date: fmtDate(header.due_date) || '',
      terms: header.terms || 'Net 30',
      memo: header.memo || '',
      ar_account_code: header.ar_account_code || '',
      status: header.status || 'Draft',
      je_id: header.je_id || null,
      void_je_id: header.void_je_id || null,
      void_reason: header.void_reason || '',
      total_amount: header.total_amount,
      paid_to_date: header.paid_to_date,
      credited_to_date: header.credited_to_date,
      balance_amount: header.balance_amount,
      source_quote_id: header.source_quote_id || null,
    })
    const nextLines = (data?.lines || []).map(normalizeLine)
    setLines(nextLines.length ? nextLines : [emptyLine()])
    setSettlements({ payments: data?.payments || [], credits: data?.credits || [] })
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setFormError('')
      try {
        const [customerRows, productRows, accountRows, companyRow, controlRows] = await Promise.all([
          getCustomers(),
          getProducts({ limit: 500 }),
          getAccounts().catch(() => []),
          getCompany().catch(() => null),
          getControlAccounts().catch(() => []),
        ])
        if (cancelled) return
        const productList = Array.isArray(productRows) ? productRows : []
        setCustomers(Array.isArray(customerRows) ? customerRows : [])
        setProducts(productList)
        setAccounts(Array.isArray(accountRows) ? accountRows : [])
        setCompany(companyRow || null)
        setControls(Object.fromEntries((Array.isArray(controlRows) ? controlRows : []).map((r) => [r.name, r.account_code])))
        if (isNew) {
          setInvoice((prev) => ({
            ...prev,
            due_date: prev.due_date || dueDateForTerms(prev.invoice_date, prev.terms),
          }))
          // A company that keeps stock starts each line as a product.
          const firstType = productList.some((p) => !isServiceItem(p)) ? 'Product' : 'Service'
          setLines((prev) => prev.map((line) => (line.product_id || line.item_name ? line : { ...line, line_type: firstType })))
        } else {
          const data = await getCustomerInvoice(invoiceId)
          if (cancelled) return
          applyServerInvoice(data)
        }
      } catch (e) {
        if (!cancelled) setFormError(e.message || 'Failed to load invoice')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [invoiceId, isNew])

  const updateInvoice = (patch) => setInvoice((prev) => ({ ...prev, ...patch }))
  const [depositAvailable, setDepositAvailable] = useState(0)
  const [jobRetainagePct, setJobRetainagePct] = useState(0)
  useEffect(() => {
    if (!invoice.job_id || kind === 'Deposit' || kind === 'Retainage' || readOnly) { setDepositAvailable(0); setJobRetainagePct(0); return undefined }
    let cancelled = false
    jobDeposits(invoice.job_id, invoice.invoice_id).then((d) => { if (!cancelled) { setDepositAvailable(num(d?.available)); setJobRetainagePct(num(d?.retainage_percent)) } }).catch(() => {})
    return () => { cancelled = true }
  }, [invoice.job_id, invoice.invoice_id, kind, readOnly])
  const updateLine = (idx, patch) => setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)))
  const addLine = () => setLines((prev) => [...prev, emptyLine(newLineType)])
  const removeLine = (idx) => {
    if (lines.length <= 1) {
      notify('At least one line item is required')
      return
    }
    setLines((prev) => prev.filter((_, i) => i !== idx))
  }

  const lineCalc = (line) => {
    const gross = round2(num(line.quantity) * num(line.unit_price))
    const discount = Math.min(Math.max(round2(line.discount_amount), 0), gross)
    const net = round2(gross - discount)
    const tax = round2(net * (Math.max(0, num(line.tax_rate)) / 100))
    return { gross, discount, net, tax, total: round2(net + tax) }
  }

  const totals = useMemo(() => lines.reduce((acc, line) => {
    const calc = lineCalc(line)
    acc.subtotal += calc.gross
    acc.discount += calc.discount
    acc.tax += calc.tax
    acc.total += calc.total
    return acc
  }, { subtotal: 0, discount: 0, tax: 0, total: 0 }), [lines])
  const hasProductLines = useMemo(() => lines.some((line) => line.line_type === 'Product'), [lines])
  // Opens by itself when a line being edited uses an account other than its default.
  const accountsVisible = showAccounts || (!readOnly && lines.some((line) => line.revenue_account_code && line.revenue_account_code !== revenueFor(line)))
  // A paid deposit on the job can be taken off this invoice.
  const depositTaken = Math.min(num(invoice.deposit_applied), totals.total)
  // Retainage the customer holds back until the job is accepted.
  const workBeforeTax = round2(totals.subtotal - totals.discount)
  const retainageHeld = Math.min(num(invoice.retainage_amount), round2(totals.total - depositTaken))
  const retainageSuggested = round2(workBeforeTax * jobRetainagePct / 100)
  const toPay = round2(totals.total - depositTaken - retainageHeld)
  const depositRow = kind !== 'Deposit' && kind !== 'Retainage' && (depositTaken > 0.004 || (!readOnly && depositAvailable > 0.004))
  const printAmountDue = readOnly ? invoiceOpenBalance : toPay
  const printInvoiceTotal = readOnly ? num(invoice.total_amount ?? toPay) : toPay
  const received = [
    ...settlements.payments
      .filter((p) => String(p.status || 'Posted').toLowerCase() !== 'void')
      .map((p) => ({ key: `p${p.payment_id}`, when: parseWallClock(p.payment_date), label: p.method || 'Payment', detail: p.reference_no, amount: num(p.amount_applied), paymentId: p.payment_id, paymentNo: p.payment_no })),
    ...settlements.credits
      .filter((c) => String(c.status || 'Posted').toLowerCase() === 'posted')
      .map((c) => ({ key: `c${c.credit_memo_id}`, when: parseWallClock(c.credit_date), label: `Credit memo ${c.credit_memo_number || ''}`.trim(), detail: c.reason, amount: num(c.total_amount) })),
  ].sort((a, b) => (a.when?.getTime() || 0) - (b.when?.getTime() || 0))
  const paymentCount = received.filter((r) => r.paymentId).length
  const creditCount = received.length - paymentCount
  const receivedSummary = [
    paymentCount && `${paymentCount} ${paymentCount === 1 ? 'payment' : 'payments'}`,
    creditCount && `${creditCount} ${creditCount === 1 ? 'credit' : 'credits'}`,
  ].filter(Boolean).join(', ')
  const printDueDate = isDueOnReceipt ? 'On receipt' : (fmtDay(invoice.due_date || dueDateForTerms(invoice.invoice_date, invoice.terms)) || '')

  // Product lines come off stock; service lines don't. Changing the type
  // keeps a chosen revenue account but moves a default one with it.
  const setLineType = (idx, lineType) => {
    const line = lines[idx]
    const product = products.find((row) => String(row.product_id) === String(line.product_id))
    const keepItem = product && (lineType === 'Service') === isServiceItem(product)
    updateLine(idx, {
      line_type: lineType,
      product_id: keepItem ? line.product_id : '',
      revenue_account_code: line.revenue_account_code === revenueFor(line) ? '' : line.revenue_account_code,
    })
  }

  const selectProduct = (idx, productId) => {
    const product = products.find((row) => String(row.product_id) === String(productId))
    if (!product) {
      updateLine(idx, { product_id: '' })
      return
    }
    const taxRate = product.is_taxable === false || String(product.is_taxable) === '0' ? '0' : String(product.tax_rate ?? '0')
    updateLine(idx, {
      line_type: isServiceItem(product) ? 'Service' : 'Product',
      product_id: String(product.product_id),
      item_name: product.name || '',
      description: product.description || '',
      unit_price: moneyInput(product.unit_price),
      tax_rate: taxRate,
    })
  }

  const setTerms = (terms) => {
    updateInvoice({
      terms,
      due_date: dueDateForTerms(invoice.invoice_date, terms) || invoice.due_date,
    })
  }

  const setInvoiceDate = (invoiceDate) => {
    updateInvoice({
      invoice_date: invoiceDate,
      due_date: dueDateForTerms(invoiceDate, invoice.terms) || invoice.due_date,
    })
  }

  const buildPayload = () => ({
    invoice_id: invoice.invoice_id,
    invoice_number: invoice.invoice_number,
    customer_id: invoice.customer_id ? Number(invoice.customer_id) : null,
    job_id: invoice.job_id ? Number(invoice.job_id) : null,
    deposit_applied: num(invoice.deposit_applied),
    retainage_amount: num(invoice.retainage_amount),
    invoice_date: invoice.invoice_date,
    due_date: invoice.due_date || null,
    terms: invoice.terms,
    memo: invoice.memo,
    ar_account_code: invoice.ar_account_code || '',
    lines: lines.map((line) => ({
      product_id: line.product_id ? Number(line.product_id) : null,
      item_name: line.item_name,
      description: line.description,
      quantity: num(line.quantity),
      unit_price: num(line.unit_price),
      discount_amount: num(line.discount_amount),
      tax_rate: num(line.tax_rate),
      revenue_account_code: line.revenue_account_code || revenueFor(line),
      line_type: line.line_type === 'Product' ? 'Product' : 'Service',
    })),
  })

  const validate = () => {
    if (!invoice.customer_id) return 'Choose a customer.'
    if (!invoice.invoice_date) return 'Enter the invoice date.'
    if (!lines.length) return 'Add at least one item.'
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i]
      if (!String(line.item_name || '').trim()) return `Item ${i + 1}: give it a name.`
      if (num(line.quantity) <= 0) return `Item ${i + 1}: the quantity must be more than zero.`
      if (num(line.unit_price) < 0) return `Item ${i + 1}: the price can't be negative.`
      if (line.line_type === 'Product' && !line.product_id) return `Item ${i + 1}: pick the product to take from stock, or make it a service.`
    }
    return ''
  }

  const saveDraft = async ({ silent = false } = {}) => {
    const err = validate()
    if (err) {
      setFormError(err)
      return null
    }
    setSaving(true)
    setFormError('')
    try {
      const saved = await saveCustomerInvoice(buildPayload())
      applyServerInvoice(saved)
      const savedId = saved?.invoice_id || saved?.header?.invoice_id
      if (!silent) notify('Draft saved')
      if (savedId && isNew) nav(`/sales/customer-invoices/${savedId}`, { replace: true })
      return savedId || invoice.invoice_id
    } catch (e) {
      setFormError(e.message || 'Failed to save invoice')
      return null
    } finally {
      setSaving(false)
    }
  }

  const postInvoice = async () => {
    const savedId = await saveDraft({ silent: true })
    if (!savedId) return
    setPosting(true)
    setFormError('')
    try {
      const posted = await postCustomerInvoice(savedId)
      notify('Invoice posted')
      const refreshed = await getCustomerInvoice(savedId)
      applyServerInvoice(refreshed)
      nav(`/sales/customer-invoices/${savedId}`, { replace: true })
    } catch (e) {
      setFormError(e.message || 'Failed to post invoice')
    } finally {
      setPosting(false)
    }
  }

  const refreshInvoice = async (id = invoice.invoice_id) => {
    if (!id) return
    const refreshed = await getCustomerInvoice(id)
    applyServerInvoice(refreshed)
  }

  const openCreditMemo = () => {
    setCreditForm({
      credit_date: todayIso(),
      reason: '',
      memo: '',
      amount: invoiceOpenBalance > 0 ? invoiceOpenBalance.toFixed(2) : '',
      tax_rate: '0',
      // An invoice of services only is credited back to Service Revenue.
      revenue_account_code: lines.every((line) => line.line_type === 'Service') ? serviceRevenue : defaultRevenue,
    })
    setCreditOpen(true)
  }

  const submitCreditMemo = async () => {
    if (!invoice.invoice_id) return
    if (!String(creditForm.reason || '').trim()) {
      setFormError('Give a reason for the credit.')
      return
    }
    const amount = round2(creditForm.amount)
    if (amount <= 0) {
      setFormError('The credit must be more than zero.')
      return
    }
    if (amount - invoiceOpenBalance > 0.004) {
      setFormError('The credit can’t be more than the customer still owes.')
      return
    }
    setCrediting(true)
    setFormError('')
    try {
      const saved = await createCustomerCreditMemo({
        invoice_id: invoice.invoice_id,
        credit_date: creditForm.credit_date,
        reason: creditForm.reason,
        memo: creditForm.memo,
        lines: [{
          item_name: creditForm.reason || 'Customer credit',
          description: creditForm.memo,
          quantity: 1,
          unit_price: amount,
          tax_rate: num(creditForm.tax_rate),
          revenue_account_code: creditForm.revenue_account_code || defaultRevenue,
        }],
      })
      notify(saved.credit_memo_number ? `Credit memo ${saved.credit_memo_number} posted` : 'Credit memo posted')
      setCreditOpen(false)
      await refreshInvoice(invoice.invoice_id)
    } catch (e) {
      setFormError(e.message || 'Failed to create credit memo')
    } finally {
      setCrediting(false)
    }
  }

  const submitVoidInvoice = async () => {
    if (!invoice.invoice_id) return
    if (!String(voidReason || '').trim()) {
      setFormError('Give a reason for voiding.')
      return
    }
    setVoiding(true)
    setFormError('')
    try {
      const result = await voidCustomerInvoice(invoice.invoice_id, { reason: voidReason })
      notify('Invoice voided')
      setVoidOpen(false)
      setVoidReason('')
      await refreshInvoice(invoice.invoice_id)
    } catch (e) {
      setFormError(e.message || 'Failed to void invoice')
    } finally {
      setVoiding(false)
    }
  }

  const printInvoice = async () => {
    notify('Preparing the invoice to print')
    if (typeof window === 'undefined') return
    await preloadImage(companyLogoSrc)
    const cleanup = () => document.body.classList.remove('ci-printing')
    document.body.classList.add('ci-printing')
    window.addEventListener('afterprint', cleanup, { once: true })
    setTimeout(() => {
      window.print()
      setTimeout(cleanup, 1200)
    }, 50)
  }
  useAutoPrint(!loading && !isNew, printInvoice)


  return (
    <div className={`ci-form-page ${readOnly ? 'posted' : ''}`}>
      <DocHeader
        back={<BackLink to="/sales/customer-invoices" label="Invoices" />}
        title={invoiceNumber}
        badge={<StatusBadge label={badge.label} tone={badge.tone} />}
        meta={!loading && (
          <>
            {invoice.customer_id ? customerName : 'No customer yet'}
            {invoice.invoice_date && ` · ${fmtDay(invoice.invoice_date)}`}
            {kindLabel && ` · ${kindLabel}`}
            {invoice.source_quote_id && <> · <Link to={`/sales/quotes/${invoice.source_quote_id}`}>From quote {invoice.source_quote_number || ''}</Link></>}
            {books && invoice.je_id && <> · <Link to={`/accounting/journal/${invoice.je_id}`}>Journal entry {docNo('je', invoice.je_no)}</Link></>}
            {books && invoice.void_je_id && <> · <Link to={`/accounting/journal/${invoice.void_je_id}`}>Void entry {docNo('je', invoice.void_je_no)}</Link></>}
          </>
        )}
        actions={canRecordPayment && (
          <Link className="ci-btn ci-btn-primary" to={`/sales/customer-payments?customer_id=${encodeURIComponent(invoice.customer_id)}&invoice_id=${encodeURIComponent(invoice.invoice_id)}`}><Wallet size={14} />Record payment</Link>
        )}
      />

      {formError && <ErrorBanner message={formError} />}
      {jobOnly && !invoice.job_id && !readOnly && (
        <p className="doc-hint job-only-hint">Your plan bills from jobs: choose the job this invoice is for below, or <Link to="/jobs">start from a job</Link>.</p>
      )}
      {loading ? (
        <Spinner label="Loading invoice..." />
      ) : (
        <>
          <div className="ci-form-meta-card">
            <div className="ci-form-meta-top">
              <label className="ci-form-field">
                <span>Customer</span>
                <select disabled={linesLocked} value={invoice.customer_id} onChange={(e) => updateInvoice({ customer_id: e.target.value })}>
                  <option value="">Choose a customer…</option>
                  <CreateOption kind="customer" />
                  {customers.map((customer) => (
                    <option key={customer.customer_id} value={customer.customer_id}>{customerLabel(customer)}</option>
                  ))}
                </select>
              </label>
              <JobSelect customerId={invoice.customer_id} value={invoice.job_id} disabled={linesLocked} onChange={(v) => updateInvoice({ job_id: v })} />
              <label className="ci-form-field">
                <span>Invoice number</span>
                <input disabled={readOnly} value={invoice.invoice_number} onChange={(e) => updateInvoice({ invoice_number: e.target.value })} placeholder="Leave blank to number it for you" />
              </label>
              <label className="ci-form-field">
                <span>Invoice date</span>
                <input disabled={readOnly} type="date" value={invoice.invoice_date} onChange={(e) => setInvoiceDate(e.target.value)} />
              </label>
              <label className="ci-form-field">
                <span>Due date</span>
                <input disabled={readOnly} type="date" value={invoice.due_date || ''} onChange={(e) => updateInvoice({ due_date: e.target.value })} />
              </label>
            </div>
            <div className="ci-form-meta-bottom">
              <label className="ci-form-field">
                <span>Payment terms</span>
                <select disabled={readOnly} value={invoice.terms} onChange={(e) => setTerms(e.target.value)}>
                  <option>Due on receipt</option>
                  <option>Net 7</option>
                  <option>Net 15</option>
                  <option>Net 30</option>
                  <option>Net 45</option>
                  <option>Net 60</option>
                  <option>Net 90</option>
                </select>
              </label>
              {books && (receivables?.length > 1 || (invoice.ar_account_code && readOnly)) && (
                <label className="ci-form-field">
                  <span>Receivable account <HelpTip text="Which of your receivables accounts this invoice is owed on. Payments, credits and voids for it use the same account." /></span>
                  <DefaultAccountSelect kinds="AR" accounts={receivables || []} disabled={readOnly} value={invoice.ar_account_code} onChange={(v) => updateInvoice({ ar_account_code: v })} />
                </label>
              )}
              <label className="ci-form-field ci-correction-wide">
                <span>Note to customer</span>
                <textarea
                  className="ci-form-memo-textarea"
                  disabled={readOnly}
                  rows={2}
                  value={invoice.memo}
                  onChange={(e) => updateInvoice({ memo: e.target.value })}
                  placeholder={readOnly ? '' : 'Printed on the invoice, e.g. Thank you for your business.'}
                />
              </label>
            </div>
          </div>

          <div className="ci-form-lines-card">
            <div className="ci-form-lines-head">
              <div className="ci-form-lines-title">Items{linesLocked && !readOnly && <span className="cell-sub"> · From the {kind === 'Deposit' ? 'deposit' : kind === 'Retainage' ? 'retainage held on the job' : 'quote'}, so they can’t be changed. To bill differently, void this draft and start again.</span>}</div>
              <label className="ci-form-toggle">
                <input type="checkbox" checked={accountsVisible} onChange={(e) => setShowAccounts(e.target.checked)} disabled={accountsVisible && !showAccounts} />
                Show revenue accounts
              </label>
            </div>
            <div className="ci-form-table-wrap">
              <table className="ci-form-line-table lines-cards">
                <thead>
                  <tr>
                    <th className="col-type"><span className="label-with-help">Type<HelpTip text={has('inventory') ? "Product: something you keep in stock. It comes off stock, and its cost is recorded, when the invoice is posted. Service: work you do, such as labour, delivery or repairs. Nothing comes off stock. Products and services are reported apart on the income statement." : "Product: goods you sell. Service: work you do, such as labour, delivery or repairs. They are reported apart on the income statement."} /></span></th>
                    <th className="col-product">Item</th>
                    <th className="col-name">Item name</th>
                    <th>Description</th>
                    <th className="num col-qty">Qty</th>
                    <th className="num col-price">Price</th>
                    <th className="num col-discount">Discount</th>
                    <th className="num col-tax">Tax %</th>
                    {accountsVisible && <th className="col-account">Revenue account</th>}
                    <th className="num col-amount">Amount</th>
                    <th className="col-del"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => {
                    const calc = lineCalc(line)
                    const isProduct = line.line_type === 'Product'
                    const choices = isProduct ? stockItems : serviceItems
                    const current = line.product_id && !choices.some((p) => String(p.product_id) === String(line.product_id))
                      ? products.find((p) => String(p.product_id) === String(line.product_id)) : null
                    return (
                      <tr key={`${idx}-${line.product_id || 'custom'}`}>
                        <td data-label="Type">
                          <select className="ci-form-cell-select" disabled={linesLocked} value={line.line_type} onChange={(e) => setLineType(idx, e.target.value)} aria-label={`Item ${idx + 1} type`}>
                            <option value="Product">Product</option>
                            <option value="Service">Service</option>
                          </select>
                        </td>
                        <td data-label="Item">
                          <select className="ci-form-cell-select" disabled={linesLocked} value={line.product_id} onChange={(e) => selectProduct(idx, e.target.value)} aria-label={`Item ${idx + 1}`}>
                            <option value="">{linesLocked ? '—' : isProduct ? 'Choose a product' : 'Type it in, or choose a service'}</option>
                            <CreateOption kind="product" />
                            {[...(current ? [current] : []), ...choices].map((product) => (
                              <option key={product.product_id} value={product.product_id}>
                                {product.sku ? `${product.sku} - ` : ''}{product.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td data-label="Item name"><input className="ci-form-cell-input" disabled={linesLocked} value={line.item_name} onChange={(e) => updateLine(idx, { item_name: e.target.value })} placeholder={linesLocked ? '' : 'Item name'} /></td>
                        <td data-label="Description"><input className="ci-form-cell-input" disabled={linesLocked} value={line.description} onChange={(e) => updateLine(idx, { description: e.target.value })} placeholder={linesLocked ? '' : 'Description'} /></td>
                        <td data-label="Qty" className="lc-half"><input className="ci-form-cell-input num" disabled={linesLocked} value={line.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} /></td>
                        <td data-label="Price" className="lc-half"><input className="ci-form-cell-input num" disabled={linesLocked} value={line.unit_price} onChange={(e) => updateLine(idx, { unit_price: e.target.value })} /></td>
                        <td data-label="Discount" className="lc-half"><input className="ci-form-cell-input num" disabled={linesLocked} value={line.discount_amount} onChange={(e) => updateLine(idx, { discount_amount: e.target.value })} /></td>
                        <td data-label="Tax %" className="lc-half"><input className="ci-form-cell-input num" disabled={linesLocked} value={line.tax_rate} onChange={(e) => updateLine(idx, { tax_rate: e.target.value })} /></td>
                        {accountsVisible && (
                          <td data-label="Revenue account">
                            <select className="ci-form-cell-select" disabled={linesLocked} value={line.revenue_account_code || revenueFor(line)} onChange={(e) => updateLine(idx, { revenue_account_code: e.target.value })}>
                              {revenueAccounts.map((account) => (
                                <option key={account.account_id || account.account_code} value={account.account_code}>
                                  {account.account_code} - {account.account_name}
                                </option>
                              ))}
                            </select>
                          </td>
                        )}
                        <td className="ci-form-cell-amount" data-label="Amount">{money(calc.total)}</td>
                        <td className="ci-form-del-cell">
                          {!linesLocked && (
                            <button type="button" className="ci-form-del-btn" onClick={() => removeLine(idx)} aria-label="Remove line">
                              <Trash2 size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {!linesLocked && (
              <div className="ci-form-add-row">
                <button type="button" className="ci-form-add-line" onClick={addLine}>
                  <Plus size={14} />
                  Add item
                </button>
              </div>
            )}
          </div>

          <div className="ci-form-bottom-row">
            <div className="ci-form-hint">
              {hasProductLines && (
                <p>
                  <Info size={14} aria-hidden="true" />
                  <span><strong>Product</strong> lines come off your stock, and their cost is recorded, when you post the invoice.</span>
                </p>
              )}
            </div>

            <div className="ci-form-totals">
              <div className="ci-form-total-row">
                <span>Subtotal</span>
                <strong>{money(totals.subtotal)}</strong>
              </div>
              <div className="ci-form-total-row discount">
                <span>Discounts</span>
                <strong>{totals.discount > 0 ? `-${money(totals.discount)}` : '-'}</strong>
              </div>
              <div className="ci-form-total-row tax">
                <span>Tax</span>
                <strong>{totals.tax > 0 ? `${money(totals.tax)}` : '-'}</strong>
              </div>
              <div className="ci-form-total-divider" />
              <div className="ci-form-grand-total">
                <span>Total</span>
                <strong>{money(totals.total)}</strong>
              </div>
              {kind !== 'Deposit' && kind !== 'Retainage' && (retainageHeld > 0.004 || (!readOnly && jobRetainagePct > 0)) && (
                <div className="ci-form-total-row discount">
                  <span>
                    Retainage held back <HelpTip text="The share the customer keeps until the job is accepted. The work is still earned in full; they pay this part later, on a retainage invoice from the job." />
                    {!readOnly && jobRetainagePct > 0 && (
                      <span className="cell-sub"> · <button type="button" className="ci-link-btn" onClick={() => updateInvoice({ retainage_amount: String(retainageSuggested) })}>Hold back {jobRetainagePct}%</button></span>
                    )}
                  </span>
                  {readOnly ? <strong>-{money(retainageHeld)}</strong> : (
                    <input className="ci-deposit-input" inputMode="decimal" value={invoice.retainage_amount} placeholder="0.00"
                      onChange={(e) => updateInvoice({ retainage_amount: e.target.value })} aria-label="Retainage held back" />
                  )}
                </div>
              )}
              {depositRow && (
                <>
                  <div className="ci-form-total-row discount">
                    <span>
                      Deposit received <HelpTip text="A deposit the customer already paid on this job. Taking it off here lowers what they owe on this invoice; it was held as customer deposits until now." />
                      {!readOnly && depositAvailable > 0.004 && (
                        <span className="cell-sub"> {money(depositAvailable)} paid on the job · <button type="button" className="ci-link-btn" onClick={() => updateInvoice({ deposit_applied: String(Math.min(depositAvailable, totals.total)) })}>Take off all</button></span>
                      )}
                    </span>
                    {readOnly ? <strong>-{money(depositTaken)}</strong> : (
                      <input className="ci-deposit-input" inputMode="decimal" value={invoice.deposit_applied} placeholder="0.00"
                        onChange={(e) => updateInvoice({ deposit_applied: e.target.value })} aria-label="Deposit received" />
                    )}
                  </div>
                  <div className="ci-form-grand-total">
                    <span>To pay</span>
                    <strong>{money(toPay)}</strong>
                  </div>
                </>
              )}
              {!depositRow && retainageHeld > 0.004 && (
                <div className="ci-form-grand-total">
                  <span>To pay</span>
                  <strong>{money(toPay)}</strong>
                </div>
              )}
              {readOnly && (
                <>
                  <div className="ci-form-total-divider" />
                  <div className="ci-form-total-row">
                    <span>Paid</span>
                    <strong>{money(paidToDate)}</strong>
                  </div>
                  <div className="ci-form-total-row">
                    <span>Credit memos</span>
                    <strong>{money(creditedToDate)}</strong>
                  </div>
                  <div className="ci-form-grand-total">
                    <span>Still owed</span>
                    <strong>{money(invoiceOpenBalance)}</strong>
                  </div>
                </>
              )}
            </div>
          </div>

          {readOnly && received.length > 0 && (
            <section className="doc-card">
              <div className="doc-card-head">
                <h2>Payments and credits</h2>
                <span>{receivedSummary} · {money(paidToDate + creditedToDate)}</span>
              </div>
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead>
                    <tr><th>Date</th><th>Received as</th><th>Reference</th><th className="num">Amount</th></tr>
                  </thead>
                  <tbody>
                    {received.map((r) => (
                      <tr key={r.key}>
                        <td>{r.when ? fmtDay(r.when) : '–'}</td>
                        <td>{r.label}{r.paymentNo && <span className="cell-sub">Payment {docNo('customerPayment', r.paymentNo)}</span>}</td>
                        <td>{r.detail || '–'}</td>
                        <td className="num ci-total">{money(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <div className="ci-form-action-bar">
            <div className="ci-form-action-left">
              {!readOnly && (
                <>
                  <button type="button" className="ci-btn ci-btn-primary" disabled={saving || posting} onClick={postInvoice}>
                    <Check size={14} />
                    {posting ? 'Posting…' : 'Post invoice'}
                  </button>
                  <button type="button" className="ci-btn ci-btn-ghost" disabled={saving || posting} onClick={() => saveDraft()}>
                    <FileText size={14} />
                    {saving ? 'Saving…' : 'Save draft'}
                  </button>
                </>
              )}
              <button type="button" className="ci-btn ci-btn-ghost" onClick={() => printPage(printInvoice)}>
                <Printer size={14} />
                Print or save PDF
              </button>
              {readOnly && statusLower !== 'void' && (
                <span className="ci-form-posted-note">
                  Posted invoices can’t be changed. To correct one, void it and create a new invoice.
                </span>
              )}
            </div>
            <div className="ci-form-action-right">
              {canCreateCreditMemo && (
                <button type="button" className="ci-btn ci-btn-ghost" onClick={openCreditMemo}>
                  <RotateCcw size={14} />
                  Give a credit
                </button>
              )}
              {readOnly && statusLower !== 'void' && (
                <button
                  type="button"
                  className="ci-form-danger"
                  disabled={!canVoidInvoice || voiding}
                  onClick={() => setVoidOpen(true)}
                  title={canVoidInvoice ? 'Void this invoice' : 'Void its payments and credits first'}
                >
                  <XCircle size={14} />
                  {voiding ? 'Voiding…' : 'Void invoice'}
                </button>
              )}
            </div>
          </div>

          <Modal
            open={creditOpen}
            onClose={() => setCreditOpen(false)}
            title="Give a credit"
            subtitle={`Reduce what the customer owes on ${invoiceNumber}, for example after a return or a price correction.`}
            footer={
              <>
                <Button variant="ghost" onClick={() => setCreditOpen(false)}>Cancel</Button>
                <Button variant="primary" onClick={submitCreditMemo} loading={crediting}>
                  <RotateCcw size={14} />
                  Post credit memo
                </Button>
              </>
            }
          >
            <div className="ci-correction-grid">
              <label className="ci-form-field">
                <span>Credit date</span>
                <input type="date" value={creditForm.credit_date} onChange={(e) => setCreditForm((prev) => ({ ...prev, credit_date: e.target.value }))} />
              </label>
              <label className="ci-form-field">
                <span>Amount before tax</span>
                <input value={creditForm.amount} onChange={(e) => setCreditForm((prev) => ({ ...prev, amount: e.target.value }))} />
              </label>
              <label className="ci-form-field">
                <span>Tax %</span>
                <input value={creditForm.tax_rate} onChange={(e) => setCreditForm((prev) => ({ ...prev, tax_rate: e.target.value }))} />
              </label>
              <label className="ci-form-field">
                <span>Revenue account</span>
                <select value={creditForm.revenue_account_code || defaultRevenue} onChange={(e) => setCreditForm((prev) => ({ ...prev, revenue_account_code: e.target.value }))}>
                  {revenueAccounts.map((account) => (
                    <option key={account.account_id || account.account_code} value={account.account_code}>
                      {account.account_code} - {account.account_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ci-form-field ci-correction-wide">
                <span>Reason</span>
                <input value={creditForm.reason} onChange={(e) => setCreditForm((prev) => ({ ...prev, reason: e.target.value }))} placeholder="For example: goods returned, or price corrected" />
              </label>
              <label className="ci-form-field ci-correction-wide">
                <span>Note (optional)</span>
                <input value={creditForm.memo} onChange={(e) => setCreditForm((prev) => ({ ...prev, memo: e.target.value }))} placeholder="Optional internal note" />
              </label>
            </div>
            <div className="ci-correction-note">The customer still owes {money(invoiceOpenBalance)} on this invoice.</div>
          </Modal>

          <Modal
            open={voidOpen}
            onClose={() => setVoidOpen(false)}
            title={`Void ${invoiceNumber}`}
            subtitle="The invoice stays in your records marked Void, and its amounts are reversed in your books."
            footer={
              <>
                <Button variant="ghost" onClick={() => setVoidOpen(false)}>Cancel</Button>
                <Button variant="danger" onClick={submitVoidInvoice} loading={voiding}>
                  <XCircle size={14} />
                  Void invoice
                </Button>
              </>
            }
          >
            <label className="ci-form-field">
              <span>Why are you voiding it?</span>
              <input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="Kept with the invoice for your records" />
            </label>
            <div className="ci-correction-note">Only invoices with no payments or credits can be voided. Void those first.</div>
          </Modal>

          <DocumentPrint
            company={{ name: companyName, tagline: company?.tagline, logo: companyLogoSrc, address: companyAddress, contact: companyContact }}
            title="Invoice"
            number={invoiceNumber}
            status={badge.label === 'Unpaid' ? null : badge}
            billTo={{ name: customerName, lines: [...customerAddress, ...customerContact] }}
            facts={[
              ['Invoice date', fmtDay(invoice.invoice_date)],
              ['Due date', printDueDate],
              ['Terms', invoice.terms],
            ]}
            highlight={statusLower === 'void' ? null : {
              label: readOnly && printAmountDue <= 0.004 ? 'Paid in full' : 'Balance due',
              value: money(printAmountDue),
              note: printAmountDue > 0.004 && printDueDate ? (isDueOnReceipt ? 'Due on receipt' : `Due by ${printDueDate}`) : null,
            }}
            lines={lines.map((line) => {
              const calc = lineCalc(line)
              return { name: line.item_name, description: line.description, qty: num(line.quantity), unitPrice: num(line.unit_price), discount: calc.discount, tax: calc.tax, amount: calc.total }
            })}
            totals={[
              ...(totals.discount > 0.004 || totals.tax > 0.004 ? [{ label: 'Subtotal', value: totals.subtotal }] : []),
              ...(totals.discount > 0.004 ? [{ label: 'Discounts', value: totals.discount, kind: 'minus' }] : []),
              ...(totals.tax > 0.004 ? [{ label: 'Tax', value: totals.tax }] : []),
              ...(depositTaken > 0.004 || retainageHeld > 0.004 ? [{ label: 'Total for the work', value: totals.total }] : []),
              ...(depositTaken > 0.004 ? [{ label: 'Deposit received', value: depositTaken, kind: 'minus' }] : []),
              ...(retainageHeld > 0.004 ? [{ label: 'Retainage held back', value: retainageHeld, kind: 'minus' }] : []),
              { label: 'Invoice total', value: printInvoiceTotal, kind: 'grand' },
              ...(readOnly && paidToDate > 0.004 ? [{ label: 'Payments received', value: paidToDate, kind: 'minus' }] : []),
              ...(readOnly && creditedToDate > 0.004 ? [{ label: 'Credits', value: creditedToDate, kind: 'minus' }] : []),
              ...(statusLower !== 'void' ? [{ label: 'Balance due', value: printAmountDue, kind: 'due' }] : []),
            ]}
            settlements={readOnly ? received.map((r) => ({ date: r.when ? fmtDay(r.when) : '', label: r.label, detail: r.detail, amount: r.amount })) : []}
            notes={invoice.memo || 'Thank you for your business.'}
            footer={`${paymentInstruction}${companyContact.length ? ` Questions? Contact ${companyContact[0]}.` : ''}`}
          />
        </>
      )}
    </div>
  )
}
